import React, { useState } from "react";
import { Link } from "react-router-dom";
import Logo from "@/components/Logo";
import { cn } from "@/lib/utils";
import { Sheet, SheetContent, SheetTrigger } from "@/components/ui/sheet";
import { Menu, X, ArrowRight, Linkedin, Twitter } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ComesInGoesOutUnderline } from "@/components/ui/underline-animation";

// Navigation links configuration
const navLinks = [
  { title: "Home", href: "/" },
  { title: "Case Studies", href: "/case-studies" },
  { title: "Blog", href: "/blog" },
  { title: "Components", href: "/components" },
  { title: "Blocks", href: "/blocks" },
  { title: "Contact", href: "/contact" },
];

// Social links configuration
const socialLinks = [
  {
    icon: <X className="h-5 w-5" />,
    href: "https://github.com",
    label: "GitHub",
  },
  {
    icon: <Linkedin className="h-5 w-5" />,
    href: "https://linkedin.com",
    label: "LinkedIn",
  },
  {
    icon: <Twitter className="h-5 w-5" />,
    href: "https://twitter.com",
    label: "Twitter",
  },
];

const Navbar = () => {
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  return (
    <header
      className={cn(
        " top-0 left-0 right-0 z-[49] w-full transition-all duration-300",
        "py-6"
      )}
    >
      <div className="container flex items-center justify-between">
        {/* Logo */}
        <Logo variant="dark" />

        {/* Desktop Navigation */}
        <nav className="hidden md:flex items-center space-x-1">
          <ul className="flex space-x-1">
            {navLinks.map((link, index) => (
              <li key={index}>
                <Link
                  to={link.href}
                  onClick={(e) =>
                    scrollToSection(
                      e,
                      link.href.includes("#") ? link.href : null
                    )
                  }
                  className="relative px-3 py-2 text-sm font-medium transition-colors hover:text-primary rounded-md"
                >
                  <ComesInGoesOutUnderline label={link.title} />
                  {/* Hover animation line */}
                  <span className="absolute bottom-0 left-0 w-full h-[2px] bg-primary scale-x-0 transition-transform origin-left group-hover:scale-x-100"></span>
                </Link>
              </li>
            ))}
          </ul>

          <Button asChild size="sm" className="ml-4 group">
            <a href="#contact" className="inline-flex items-center gap-2">
              Let's Talk
              <ArrowRight
                className="-me-1 ms-0 transition-transform group-hover:translate-x-0.5"
                size={14}
                strokeWidth={2}
                aria-hidden="true"
              />
            </a>
          </Button>
        </nav>

        {/* Mobile Menu Trigger */}
        <Sheet open={mobileMenuOpen} onOpenChange={setMobileMenuOpen}>
          <SheetTrigger asChild className="md:hidden">
            <Button
              variant="ghost"
              size="icon"
              className="h-9 w-9 rounded-md"
              aria-label="Menu"
            >
              <Menu className="h-5 w-5" />
            </Button>
          </SheetTrigger>

          {/* Mobile Menu Content */}
          <SheetContent
            side="left"
            className="w-full max-w-xs flex flex-col justify-between p-0"
          >
            <div>
              <div className="p-6 border-b border-accent/10">
                <Logo variant="dark" />
                <Button
                  variant="ghost"
                  size="icon"
                  className="absolute top-4 right-4 h-8 w-8 rounded-md"
                  onClick={() => setMobileMenuOpen(false)}
                >
                  <X className="h-5 w-5" />
                </Button>
              </div>

              <nav className="px-2 py-4">
                <ul className="space-y-2">
                  {navLinks.map((link, index) => (
                    <li key={index}>
                      <a
                        href={link.href}
                        onClick={(e) =>
                          scrollToSection(
                            e,
                            link.href.includes("#") ? link.href : null
                          )
                        }
                        className="flex items-center px-4 py-3 text-base font-medium hover:bg-accent/10 rounded-md transition-colors"
                      >
                        {link.title}
                      </a>
                    </li>
                  ))}
                </ul>
              </nav>
            </div>

            {/* Social links in mobile menu */}
            <div className="p-6 border-t border-accent/10 mt-auto">
              <p className="text-xs text-muted-foreground mb-3">
                Connect with me
              </p>
              <div className="flex space-x-3">
                {socialLinks.map((link, index) => (
                  <a
                    key={index}
                    href={link.href}
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={link.label}
                    className="p-2 rounded-md hover:bg-accent/10 transition-colors"
                  >
                    {link.icon}
                  </a>
                ))}
              </div>
            </div>
          </SheetContent>
        </Sheet>
      </div>
    </header>
  );
};

export default Navbar;
